import { useEffect, type JSX } from 'react';

import { readAuthError, useCurrentUser } from './api/auth.ts';
import { AppShell } from './components/AppShell.tsx';
import { History } from './pages/History.tsx';
import { Owed } from './pages/Owed.tsx';
import { Setup } from './pages/Setup.tsx';
import { SignIn } from './pages/SignIn.tsx';
import { Wagers } from './pages/Wagers.tsx';
import { useRoute, type Route } from './router.tsx';
import { Alert } from './ui/Alert.tsx';
import { hintClass } from './ui/styles.ts';

/** What the browser tab says, so an open view is recognisable in a tab strip or a history. */
const documentTitles: Readonly<Record<Route, string>> = {
  '/': 'Gamble Chess — what you owe',
  '/wagers': 'Gamble Chess — wagers',
  '/history': 'Gamble Chess — history',
  '/setup': 'Gamble Chess — setup',
};

export function App(): JSX.Element {
  const currentUser = useCurrentUser();
  const route = useRoute();

  useEffect(() => {
    document.title = documentTitles[route];
  }, [route]);

  if (currentUser.isPending) {
    return (
      <AppShell user={null}>
        <p className={hintClass}>Loading…</p>
      </AppShell>
    );
  }

  if (currentUser.isError) {
    return (
      <AppShell user={null}>
        <Alert>Gamble Chess is unavailable right now. Please reload the page in a moment.</Alert>
      </AppShell>
    );
  }

  const user = currentUser.data;
  if (user === null) {
    return <SignIn authError={readAuthError(window.location.search)} />;
  }

  return (
    <AppShell user={user}>
      {route === '/' ? <Owed /> : null}
      {route === '/wagers' ? <Wagers /> : null}
      {route === '/history' ? <History /> : null}
      {route === '/setup' ? <Setup user={user} /> : null}
    </AppShell>
  );
}
